import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { ValueOf } from 'type-fest';

import { Box, Button, FieldSet, Label, LabeledControl, Legend, Radio, Tag, Text } from '@zillow/constellation-10';

import Modal from '@rental-manager/pages/Onboarding/WizardShell/WizardScreen';
import userPersonas from '@common/constants/userPersonas';
import { formatDate } from '@common/paymentsUtils/dateUtils';
import type { ILeasesForPropertyModel } from '@rental-manager/models/Types';
import IreneOnboardingCSEvents from '@rental-manager/tracking/OnboardingClickStreamEvents';
import type { ZrmClickstreamListing, ZrmClickstreamUser } from '@zg-rentals/clickstream';

const LEASE_STATUS_TAG_TONES = {
  ACTIVE: 'success',
  ARCHIVED: 'gray',
  CANCELED: 'gray',
  COUNTERSIGNING: 'critical',
  'DRAFT COMPLETED': 'warning',
  'DRAFT INCOMPLETE': 'warning',
  EXPIRED: 'gray',
  'SIGNING CANCELED': 'gray',
  'SIGNING COMPLETE': 'success',
  'SIGNING COMPLETED': 'success',
  'SIGNING IN PROGRESS': 'aqua',
} as const;

type LeaseStatusTagTone = ValueOf<typeof LEASE_STATUS_TAG_TONES>;

const LEASE_STATUS_TAG_LABELS: Record<keyof typeof LEASE_STATUS_TAG_TONES, string> = {
  ACTIVE: 'Active',
  ARCHIVED: 'Archived',
  CANCELED: 'Canceled',
  COUNTERSIGNING: 'Countersigning',
  'DRAFT COMPLETED': 'Draft completed',
  'DRAFT INCOMPLETE': 'Draft incomplete',
  EXPIRED: 'Expired',
  'SIGNING CANCELED': 'Signing canceled',
  'SIGNING COMPLETE': 'Signing complete',
  'SIGNING COMPLETED': 'Signing completed',
  'SIGNING IN PROGRESS': 'Signing in progress',
};

const getLeaseStatusTone = (status: string): LeaseStatusTagTone =>
  LEASE_STATUS_TAG_TONES[status?.trim().toUpperCase() as keyof typeof LEASE_STATUS_TAG_TONES] ?? 'gray';

const getLeaseStatusLabel = (status: string): string => {
  const normalized = status?.trim() ?? '';
  return (
    LEASE_STATUS_TAG_LABELS[normalized.toUpperCase() as keyof typeof LEASE_STATUS_TAG_LABELS] ??
    normalized.charAt(0).toUpperCase() + normalized.slice(1).toLowerCase()
  );
};

const joinTenantNames = (names: Array<string>): string => {
  if (names.length <= 2) {
    return names.join(' and ');
  }
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

const getLeaseLabel = (lease: ILeasesForPropertyModel): string => {
  const names = (lease.leaseTenants ?? [])
    .map((tenant) => `${tenant.firstName ?? ''} ${tenant.lastName ?? ''}`.trim())
    .filter(Boolean);
  if (!names.length) {
    return 'Lease';
  }
  const joined = joinTenantNames(names);
  return `${joined}${joined.endsWith('s') ? '’' : '’s'} lease`;
};

const formatLeaseDate = (value: string | undefined): string => {
  if (!value) {
    return '';
  }
  const formatted = formatDate(value, 'MMM d, yyyy');
  return formatted === 'N/A' ? '' : formatted;
};

const getLeaseTerm = (lease: ILeasesForPropertyModel): string => {
  const start = formatLeaseDate(lease.startDate);
  const end = formatLeaseDate(lease.endDate);
  if (start) {
    return `${start}–${end || 'no end date'}`;
  }
  return end ? `Through ${end}` : '';
};

type AdditionalLeaseSelectorProps = {
  index: number | null;
};

type LeaseSelectorProps = ILeasesForPropertyModel & AdditionalLeaseSelectorProps;

type Props = {
  currentProperty: ZrmClickstreamListing;
  leasesForProperty: Array<LeaseSelectorProps>;
  onBack: () => void;
  onClose: () => void;
  onSubmit: (leaseId: number | null) => void;
  propertyId: string;
  user: ZrmClickstreamUser;
};

const LeaseSelectorPage = ({
  currentProperty,
  leasesForProperty,
  onBack,
  onClose,
  onSubmit,
  propertyId,
  user,
}: Props) => {
  // @ts-ignore
  const leaseId = useSelector((state) => state.payments.landlord.onboarding[propertyId]?.leaseId);
  const [selectedLease, setSelectedLease] = useState<number | 'manual' | null>(null);

  useEffect(() => {
    // leaseId is undefined (not null) on first load, so loose != also excludes undefined —
    // otherwise selectedLease is seeded with undefined and Continue is wrongly enabled with no selection.
    if (leaseId != null) {
      setSelectedLease(leaseId);
    }
  }, [leaseId]);

  const leases: Array<LeaseSelectorProps> = leasesForProperty.map((lease, index) => ({
    ...lease,
    index,
  }));

  const submitWithoutLease = () => {
    onSubmit(null);
    IreneOnboardingCSEvents.emitContinueFromLeaseSelectorToAddPaymentManuallyEvent({
      currentProperty,
      user,
    });
  };

  const handleSubmit = () => {
    if (selectedLease === 'manual') {
      submitWithoutLease();
      return;
    }
    onSubmit(selectedLease);
    IreneOnboardingCSEvents.emitContinueWithLeaseSelectedEvent({
      currentProperty,
      user,
    });
  };

  return (
    <Modal
      persona={userPersonas.LANDLORD}
      header="Payment setup"
      onRequestClose={onClose}
      body={
        <Box
          css={{
            display: 'grid',
            gridTemplateRows: 'auto',
            gap: 'default',
          }}
        >
          <FieldSet required legend={<Legend size="lg">Select the lease you’re setting up payments for</Legend>}>
            {(leases || []).map((lease) => {
              const leaseTerm = getLeaseTerm(lease);
              return (
                <LabeledControl
                  key={lease.index}
                  control={
                    <Radio
                      name="lease-selection"
                      value={String(lease.index)}
                      checked={selectedLease === lease.index}
                      onChange={() => setSelectedLease(lease.index)}
                    />
                  }
                  label={
                    <Label>
                      {getLeaseLabel(lease)}
                      {leaseTerm && (
                        <Text css={{ color: 'text.subtle', display: 'block', marginTop: 'layout.tighter' }}>
                          {leaseTerm}
                        </Text>
                      )}
                      <Box css={{ marginTop: 'layout.tighter' }}>
                        <Tag size="sm" tone={getLeaseStatusTone(lease.status)}>
                          {getLeaseStatusLabel(lease.status)}
                        </Tag>
                      </Box>
                    </Label>
                  }
                />
              );
            })}
            <LabeledControl
              control={
                <Radio
                  name="lease-selection"
                  value="manual"
                  checked={selectedLease === 'manual'}
                  onChange={() => setSelectedLease('manual')}
                />
              }
              label={<Label>I don’t have a lease in Zillow and want to set up payments manually.</Label>}
            />
          </FieldSet>
        </Box>
      }
      backAction={{ onClick: onBack }}
      footerAriaLabel="Lease selection actions"
      footerEnd={
        <Button onClick={handleSubmit} disabled={selectedLease === null} emphasis="filled">
          Continue
        </Button>
      }
    />
  );
};

export default LeaseSelectorPage;
